import moment from 'moment';
import React from "react";
import { useSelector } from 'react-redux';

const scaleMap = {
    million: 1_000_000,
    billion: 1_000_000_000
};

const universalSorter = (valueA, valueB, profile) => {
    const extractText = (val) => {
        if (React.isValidElement(val)) {
            val = val.props?.children ?? '';
        }
        if (Array.isArray(val)) {
            val = val.map(child => {
                if (React.isValidElement(child)) {
                    return child.props?.children ?? '';
                }
                return child;
            }).join(' ');
        }
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
            const trimmed = val.trim();
            if (profile?.dateFormate && moment(trimmed, profile.dateFormate, true).isValid()) {
                return moment(trimmed, profile.dateFormate, true).toDate().getTime();
            }
            const scaleMatch = trimmed.match(/(\d+(?:\.\d+)?)\s*(million|billion)/i);
            if (scaleMatch) {
                const number = parseFloat(scaleMatch[1]);
                const unit = scaleMatch[2].toLowerCase();
                return number * scaleMap[unit];
            }
            const cleaned = trimmed
                .replace(/^[^\d-]+/, '')
                .replace(/,/g, '')
                .replace(/\s+/g, '');
            const numberMatch = cleaned.match(/^(\d+(\.\d+)?)/);
            if (numberMatch) {
                return parseFloat(numberMatch[0]);
            }
            return trimmed.toLowerCase();
        }
        return '';
    };
    const a = extractText(valueA);
    const b = extractText(valueB);
    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }
    return String(a).localeCompare(String(b), undefined, { numeric: true });
};
export const generateDynamicColumns = (checkboxValues, ViewDetailPage, sortedInfo, defaultCurrency) => {
    const localData = useSelector((state) => state.localRedux);
    const Role = localData.role
    const ProfileData = localData.profile
    const profile = ProfileData
    let columns = [];
    if (checkboxValues !== undefined) {
        columns = checkboxValues
            .filter((item) => item.isChecked)
            .map((item) => {
                const column = {
                    title: item.fieldType === "Currency" ? `${item.name} (${defaultCurrency?.currency})` : (item.fieldType === "Percentage" ? `${item.name} (%)` : item.name),
                    dataIndex: item.fieldKey,
                    key: item.fieldKey,
                    sortDirections: ['ascend', 'descend'],
                    sortOrder: sortedInfo?.column?.dataIndex === item.fieldKey ? sortedInfo?.order : null,
                };
                switch (item.FieldValue) {
                    case 'parentOrganization':
                        column.onCell = (record) => ({
                            onClick: (e) => {
                                e.stopPropagation();
                                if (record?.LinkAccount) {
                                    ViewDetailPage(record.accountID, "ACCOUNT")
                                }
                            },
                        });
                        column.sorter = (a, b) => {
                            const extractNameText = (item) => {
                                const children = item?.parentOrganization?.props?.children || [];
                                const figcaption = children.find(child => child && child.type === 'figcaption');
                                return figcaption ? figcaption.props.children : '';
                            };
                            const valueA = extractNameText(a) || '';
                            const valueB = extractNameText(b) || '';
                            return valueA.localeCompare(valueB);
                        };
                        break;
                    case 'accountOwner':
                        column.onCell = (record) => ({
                            onClick: (e) => {
                                e.stopPropagation();
                                if (record.userId) {
                                    if (Role === "Administrator" || profile.userId === Number(record.userId)) {
                                        ViewDetailPage(record.userId, "USER")
                                    }
                                }
                                else {
                                    ViewDetailPage(record.key, "ACCOUNT");
                                }
                            },
                        });
                        column.sorter = (a, b) => {
                            const extractNameText = (item) => {
                                const children = item?.accountOwner?.props?.children || [];
                                const figcaption = children.find(child => child && child.type === 'figcaption');
                                // const figcaption = item.accountOwner.props.children.find(child => child.type === 'figcaption');
                                return figcaption ? figcaption.props.children[0].props.children.trim() : ''; // Extract first div inside figcaption
                            };
                            const valueA = extractNameText(a);
                            const valueB = extractNameText(b);
                            return valueA.localeCompare(valueB);
                        };
                        break;
                    case 'accountName':
                        column.sorter = (a, b) => {
                            const extractNameText = (item) => {
                                const figcaption = item.accountName.props.children.find(child => child.type === 'figcaption');
                                return figcaption ? figcaption.props.children[0].props.children.trim() : ''; // Extract first div inside figcaption
                            };
                            const valueA = extractNameText(a);
                            const valueB = extractNameText(b);
                            return valueA.localeCompare(valueB);
                        };
                        break;
                    default:
                        column.sorter = (a, b) => {
                            const valueA = a[item.FieldValue]?.props?.children || '';
                            const valueB = b[item.FieldValue]?.props?.children || '';
                            return universalSorter(valueA, valueB, profile);
                        };
                        break;
                }
                return column;
            });
    }
    return columns;
};

export const generateUsersTableData = (exportData, checkboxValues, defaultCurrency) => {
    const localData = useSelector((state) => state.localRedux);
    const Role = localData.role
    const ProfileData = localData.profile
    const profile = ProfileData
    const usersTableData = [];
    if (exportData) {
        exportData.forEach((account, key) => {
            const customFields = {};
            account.customFieldValues?.forEach((attr) => {
                const fieldConfig = checkboxValues.find(
                    (field) => field.id === attr.fieldId
                );
                if (!fieldConfig) return;
                let displayValue = attr.value;
                if (fieldConfig.fieldType === "Currency") {
                    displayValue = `${defaultCurrency?.symbol} ${Number(attr.value || 0)?.toLocaleString()}`
                } else if (fieldConfig.fieldType === "Percentage") {
                    displayValue = `% ${attr.value ?? "0"}`;
                } else if (
                    (fieldConfig.fieldType === "Dropdown" || fieldConfig.fieldType === 'Multi-Dropdown')
                ) {
                    displayValue = attr.option;
                }
                customFields[fieldConfig.fieldKey] = (
                    <div className="table_list_clicks">
                        {displayValue}
                    </div>
                );
            });
            usersTableData.push({
                LinkAccount: account.parentAccountPermission,
                AccountDataForPermission: account,
                SharingData: account?.sharingPermission?.sharePermission,
                sharingid: account.fromId,
                userId: Number(account.accountOwner),
                accountID: account.parentOrganization ? Number(account.parentOrganization) : account.account_id,
                key: account.account_id,
                no: key + 1,
                accountName: (
                    <div className="user-info_image table_list_clicks">
                        {account.account_id && account.accountProfile !== null && account.accountProfile !== undefined && account.accountProfile !== "" ?
                            < figure >
                                <img style={{ width: "30px", height: "30px" }} src={account.accountProfile} alt="" />
                            </figure>
                            :
                            <span className='list_span_Tag AccountIcon'>
                                {account.accountName && account.accountName.slice(0, 1).toUpperCase()}
                            </span>
                        }
                        {account.account_id &&
                            <figcaption>
                                <div>
                                    {account.accountName}
                                </div>
                                <div className='entityemail'>
                                    {account.accountNumber}
                                </div>
                            </figcaption>
                        }
                    </div >),
                accountNumber: (<div className='table_list_clicks'>{`${account.accountNumber}`}</div>),
                parentOrganization: (
                    <div className={`user-info_image ${Number(account.parentAccountPermission) && "ListNavigate"}`}>
                        {account.parentOrganization &&
                            (account.parentAccountProfile !== null && account.parentAccountProfile !== undefined && account.parentAccountProfile !== "" ?
                                < figure >
                                    <img style={{ width: "30px", height: "30px" }} src={account.parentAccountProfile} alt="" />
                                </figure>
                                :
                                <span className='list_span_Tag AccountIcon'>
                                    {account.parentAccount && account.parentAccount.slice(0, 1).toUpperCase()}
                                </span>)
                        }
                        {account.parentOrganization &&
                            <figcaption>
                                <div>
                                    {account.parentAccount}
                                </div>
                                <div className='entityemail'>
                                    {account.parentAccountNumber}
                                </div>
                            </figcaption>
                        }
                    </div >
                ),
                accountOwner: (<div className={`user-info_image ${(Role === "Administrator" || profile.userId === Number(account.accountOwner)) && "ListNavigate"}`}>
                    {account.accountOwner &&
                        (account.userProfilePic !== null && account.userProfilePic !== undefined && account.userProfilePic !== "" ?
                            < figure >
                                <img style={{ width: "30px", height: "30px" }} src={account.userProfilePic} alt="" />
                            </figure>
                            :
                            <span className='list_span_Tag UserIcon'>
                                {account.userFirstName && account.userFirstName.slice(0, 1).toUpperCase()}
                            </span>
                        )}
                    {account.accountOwner &&
                        <figcaption>
                            <div>
                                {`${account.userFirstName} ${account.userLastName}`}
                            </div>
                            <div className='entityemail'>
                                {account?.accountOwnerEmail}
                            </div>
                        </figcaption>
                    }
                </div>),
                website: (<div className='table_list_clicks'>{account.website}</div>),
                accountType: (<div className='table_list_clicks'>{(account?.accountType && account.accountType !== "") ? account.accountTypeName : ""}</div>),
                industry: (<div className='table_list_clicks'>{(account?.industry && account.industry !== "") ? account.industryName : ""}</div>),
                ownership: (<div className='table_list_clicks'>{(account?.ownership && account.ownership !== "") ? account.ownershipName : ""}</div>),
                description: (<div className='table_list_clicks'>{account.description ? `${account.description.substring(0, 50)}...` : account.description}</div>),
                nomOfEmployees: (<div className='table_list_clicks'>{(account?.nomOfEmployees && account.nomOfEmployees !== "") ? account.nomOfEmployeesName : ""}</div>),
                annualRevenue: (<div className='table_list_clicks'>{(account?.annualRevenue && account.annualRevenue !== "") ? account.annualRevenueName : ""}</div>),
                customerStatus: (<div className='table_list_clicks'>{(account?.customerStatus && account?.customerStatus !== "") ? account.customerStatusName : ""}</div>),
                rating: (<div className='table_list_clicks'>{(account.rating && account.rating !== "") ? account.ratingName : ""}</div>),
                country: (<div className='table_list_clicks'>{account.country}</div>),
                state: (<div className='table_list_clicks'>{account.state}</div>),
                city: (<div className='table_list_clicks'>{account.city}</div>),
                street: (<div className='table_list_clicks'>{account.street}</div>),
                zipCode: (<div className='table_list_clicks'>{account.postcode}</div>),
                created_at: (<div className='table_list_clicks'>
                    {account?.created_at}
                </div>),
                modified_at: (<div className='table_list_clicks'>
                    {account?.modified_at}
                </div>),
                account_score: (
                    <div className='table_list_clicks'>
                        {Number(account?.accountScore)?.toFixed(2)}
                    </div>
                ),
                ...customFields,
            });
        });
    }

    return usersTableData;
};
